import Logo from "@/components/Logo";
import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Hash, Home, Users, Trophy, MessageSquare, Heart, MessageCircle, Send,
  ImagePlus, LineChart, Sparkles, Mic, X, Loader2, Flame, Medal, UserPlus, UserCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TVWidget } from "@/components/TradingView";
import { UpgradeWall } from "@/components/FeatureGate";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const j = async (path, opts = {}) => {
  const r = await fetch(`${API}/community${path}`, { credentials: "include", ...opts });
  const d = r.status === 204 ? null : await r.json().catch(() => null);
  if (!r.ok) throw new Error((d && d.detail) || "Request failed");
  return d;
};

const TAGS = [
  { id: "idea", label: "Trade Idea" },
  { id: "chart", label: "Chart" },
  { id: "journal", label: "Journal" },
  { id: "question", label: "Question" },
  { id: "update", label: "Update" },
];
const TAG_LABEL = Object.fromEntries(TAGS.map((t) => [t.id, t.label]));

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function Avatar({ name, size = 36 }) {
  const initials = (name || "T").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      className="shrink-0 rounded-full grid place-items-center font-heading text-[11px] tracking-wide text-[#0A0A0A]"
      style={{ width: size, height: size, background: "linear-gradient(135deg,#e8eef5,#9fb3c8)" }}
    >
      {initials}
    </div>
  );
}

function MiniChart({ symbol }) {
  return (
    <div className="mt-3 rounded-xl overflow-hidden border border-white/8 bg-[#0d0d0e]" data-testid="post-mini-chart">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/6">
        <LineChart className="h-3 w-3 text-[#9fd3ff]" />
        <span className="section-label text-[8px] text-zinc-400">{symbol}</span>
      </div>
      <div className="h-[150px]">
        <TVWidget
          src="https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js"
          height={150}
          config={{
            symbol, width: "100%", height: "100%", locale: "en", dateRange: "3M",
            colorTheme: "dark", isTransparent: true, autosize: true, noTimeScale: true,
            trendLineColor: "rgba(159,211,255,1)", underLineColor: "rgba(110,168,255,0.15)",
            underLineBottomColor: "rgba(110,168,255,0)",
          }}
        />
      </div>
    </div>
  );
}

function Comments({ postId }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try { setItems(await j(`/posts/${postId}/comments`)); } catch { setItems([]); }
  }, [postId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await j(`/posts/${postId}/comments`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: text.trim() }),
      });
      setText("");
      await load();
    } catch (err) { toast.error(err.message); } finally { setSending(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-white/6 space-y-3" data-testid="post-comments">
      {items === null ? (
        <div className="text-xs text-zinc-600">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-zinc-600">No replies yet. Start the discussion.</div>
      ) : (
        items.map((c) => (
          <div key={c.id} className="flex items-start gap-2.5" data-testid="comment-item">
            <Avatar name={c.author_name} size={28} />
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] text-zinc-200 font-medium">{c.author_name}</span>
                <span className="text-[10px] text-zinc-600">{timeAgo(c.created_at)}</span>
              </div>
              <div className="text-[13px] text-zinc-300 font-light whitespace-pre-wrap">{c.content}</div>
            </div>
          </div>
        ))
      )}
      <form onSubmit={submit} className="flex items-center gap-2 pt-1">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a reply…"
          className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-white/25"
          data-testid="comment-input"
        />
        <button type="submit" disabled={sending} className="btn-solid px-3 py-2 text-xs disabled:opacity-50" data-testid="comment-submit">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </button>
      </form>
    </div>
  );
}

function PostCard({ post, onLike, onDM }) {
  const [open, setOpen] = useState(false);
  const isStaffPost = ["super_admin", "admin", "coach", "content_editor"].includes(post.author_role);
  return (
    <div
      className={`glass-card p-5 ${isStaffPost ? "border !border-zinc-300/25" : ""}`}
      style={isStaffPost ? { boxShadow: "0 0 22px rgba(212,212,216,0.07), inset 0 1px 0 rgba(255,255,255,0.06)" } : undefined}
      data-testid="post-card"
    >
      <div className="flex items-start gap-3">
        <Avatar name={post.author_name} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-zinc-100 font-medium">{post.author_name}</span>
            {post.author_role && post.author_role !== "student" && (
              <span className="section-label text-[7px] text-[#9fd3ff] border border-[#9fd3ff]/30 rounded px-1.5 py-0.5">
                {post.author_role === "coach" ? "MBM Coach" : post.author_role.replace("_", " ")}
              </span>
            )}
            {post.tag && (
              <span className="section-label text-[7px] text-zinc-400 border border-white/12 rounded px-1.5 py-0.5">
                {TAG_LABEL[post.tag] || post.tag}
              </span>
            )}
            <span className="text-[10px] text-zinc-600">· {timeAgo(post.created_at)}</span>
          </div>
          <div className="mt-2 text-sm text-zinc-200 font-light leading-relaxed whitespace-pre-wrap break-words">
            {post.content}
          </div>

          {post.image_url && (
            <button onClick={() => window.open(`${API}/community/media/${post.image_url}`, "_blank")}
              className="mt-3 block w-full" data-testid="post-image">
              <img src={`${API}/community/media/${post.image_url}`} alt="chart"
                className="rounded-xl border border-white/8 max-h-[420px] w-full object-cover" />
            </button>
          )}
          {post.chart_symbol && <MiniChart symbol={post.chart_symbol} />}

          <div className="mt-4 flex items-center gap-5 text-zinc-500">
            <button onClick={() => onLike(post)} className={`flex items-center gap-1.5 text-xs transition-colors ${post.liked ? "text-[#ff5f7e]" : "hover:text-white"}`} data-testid="post-like-btn">
              <Heart className={`h-4 w-4 ${post.liked ? "fill-current" : ""}`} /> {post.likes || 0}
            </button>
            <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 text-xs hover:text-white transition-colors" data-testid="post-comment-toggle">
              <MessageCircle className="h-4 w-4" /> {post.comment_count || 0}
            </button>
            <button onClick={() => onDM(post)} className="flex items-center gap-1.5 text-xs hover:text-white transition-colors ml-auto" data-testid="post-dm-btn">
              <MessageSquare className="h-4 w-4" /> Message
            </button>
          </div>

          {open && <Comments postId={post.id} />}
        </div>
      </div>
    </div>
  );
}

function Composer({ room, onPosted }) {
  const [content, setContent] = useState("");
  const [symbol, setSymbol] = useState("");
  const [tag, setTag] = useState(null);
  const [image, setImage] = useState(null); // {path, preview}
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef();

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const d = await j("/upload", { method: "POST", body: fd });
      setImage({ path: d.url, preview: URL.createObjectURL(file) });
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!content.trim()) { toast("Write something to share"); return; }
    setPosting(true);
    try {
      await j(`/rooms/${room}/posts`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: content.trim(),
          chart_symbol: symbol.trim() || null,
          image_url: image?.path || null,
          tag,
        }),
      });
      setContent(""); setSymbol(""); setTag(null); setImage(null);
      toast.success("Posted to the community");
      onPosted();
    } catch (err) { toast.error(err.message); } finally { setPosting(false); }
  };

  return (
    <div className="glass-card p-5" data-testid="composer">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
        placeholder="Share a chart, a trade idea, or a journal reflection…"
        className="w-full bg-transparent text-sm text-zinc-100 placeholder:text-zinc-600 resize-none focus:outline-none"
        data-testid="composer-input"
      />

      {image && (
        <div className="relative mt-2 inline-block">
          <img src={image.preview} alt="preview" className="max-h-40 rounded-lg border border-white/10" />
          <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-[#0A0A0A] border border-white/20 rounded-full p-1" data-testid="composer-remove-image">
            <X className="h-3 w-3 text-zinc-300" />
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-1.5">
          <LineChart className="h-3.5 w-3.5 text-[#9fd3ff]" />
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="Tag symbol (BTCUSD)"
            className="bg-transparent text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none w-32"
            data-testid="composer-symbol"
          />
        </div>

        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1.5 bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-zinc-300 hover:border-white/25 transition-colors disabled:opacity-50"
          data-testid="composer-upload-btn">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          {uploading ? "Uploading…" : "Screenshot"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="composer-file-input" />

        <div className="flex items-center gap-1.5 flex-wrap">
          {TAGS.map((t) => (
            <button key={t.id} onClick={() => setTag(tag === t.id ? null : t.id)}
              className={`text-[10px] tracking-wide rounded-full border px-2.5 py-1 transition-colors ${tag === t.id ? "bg-[#FAFAFA] text-[#0A0A0A] border-transparent" : "border-white/12 text-zinc-400 hover:border-white/30"}`}
              data-testid={`composer-tag-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        <button onClick={submit} disabled={posting}
          className="btn-solid px-5 py-2 text-xs font-medium ml-auto disabled:opacity-50" data-testid="composer-submit">
          {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Post"}
        </button>
      </div>
    </div>
  );
}

function DMDrawer({ target, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(async () => {
    try { setMsgs(await j(`/dm/${target.user_id}`)); } catch { setMsgs([]); }
  }, [target.user_id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await j(`/dm/${target.user_id}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: text.trim() }),
      });
      setText(""); await load();
    } catch (err) { toast.error(err.message); }
  };

  const { user } = useAuth();
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="dm-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0A0A0A] border-l border-white/10 flex flex-col">
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/8">
          <div className="flex items-center gap-3">
            <Avatar name={target.name} size={32} />
            <span className="text-sm text-zinc-100 font-medium">{target.name}</span>
          </div>
          <button onClick={onClose} data-testid="dm-close"><X className="h-5 w-5 text-zinc-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {msgs.length === 0 && <div className="text-xs text-zinc-600 text-center mt-8">No messages yet. Say hello.</div>}
          {msgs.map((m) => {
            const mine = m.from_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-[13px] ${mine ? "bg-[#FAFAFA] text-[#0A0A0A]" : "bg-white/[0.06] text-zinc-200"}`}>
                  {m.content}
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={send} className="p-4 border-t border-white/8 flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message…"
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-white/25"
            data-testid="dm-input" />
          <button type="submit" className="btn-solid px-4 py-2.5" data-testid="dm-send"><Send className="h-4 w-4" /></button>
        </form>
      </div>
    </div>
  );
}

export default function Community() {
  const { user, hasFeature, entitlements } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [active, setActive] = useState("home"); // home | room-id | traders | messages
  const [posts, setPosts] = useState(null);
  const [traders, setTraders] = useState([]);
  const [convos, setConvos] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [dmTarget, setDmTarget] = useState(null);

  const isRoom = rooms.some((r) => r.id === active);
  const postRoom = isRoom ? active : "market";

  useEffect(() => {
    j("/rooms").then(setRooms).catch(() => {});
    j("/leaderboard").then(setLeaderboard).catch(() => {});
    j("/me/achievements").then(setAchievements).catch(() => {});
    j("/challenges").then(setChallenge).catch(() => {});
  }, []);

  const loadPosts = useCallback(async () => {
    if (active === "traders" || active === "messages") return;
    setPosts(null);
    try {
      const data = active === "home" ? await j("/feed") : await j(`/rooms/${active}/posts`);
      setPosts(data);
    } catch { setPosts([]); }
  }, [active]);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => {
    if (active === "traders") j("/traders").then(setTraders).catch(() => {});
    if (active === "messages") j("/dm").then(setConvos).catch(() => {});
  }, [active]);

  const refreshMeta = () => {
    j("/leaderboard").then(setLeaderboard).catch(() => {});
    j("/me/achievements").then(setAchievements).catch(() => {});
  };

  const like = async (post) => {
    try {
      const d = await j(`/posts/${post.id}/like`, { method: "POST" });
      setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, liked: d.liked, likes: d.likes } : p)));
      refreshMeta();
    } catch (err) { toast.error(err.message); }
  };

  const follow = async (t) => {
    try {
      const d = await j(`/follow/${t.id}`, { method: "POST" });
      setTraders((ts) => ts.map((x) => (x.id === t.id ? { ...x, following: d.following, followers: x.followers + (d.following ? 1 : -1) } : x)));
    } catch (err) { toast.error(err.message); }
  };

  const openDM = (target) => setDmTarget(target);

  const NavItem = ({ id, icon: Icon, label, badge }) => (
    <button onClick={() => setActive(id)}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${active === id ? "bg-white/[0.06] text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"}`}
      data-testid={`nav-${id}`}>
      <Icon className="h-4 w-4" strokeWidth={1.5} /> <span className="font-light">{label}</span>
      {badge != null && <span className="ml-auto text-[10px] text-zinc-600">{badge}</span>}
    </button>
  );

  const locked = user && entitlements !== null && !hasFeature("community");
  if (locked) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
        <div className="grain" />
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
              <span className="text-zinc-700">/</span>
              <span className="section-label text-[10px] text-[#9fd3ff]">Community</span>
            </div>
            <Avatar name={user?.name} size={30} />
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <UpgradeWall
            feature="community"
            title="Join the MBM Community"
            blurb="The Community is where members share charts, trade ideas and journals, get mentor feedback, and climb the leaderboard. It unlocks with any MBM membership."
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
            <span className="text-zinc-700">/</span>
            <span className="section-label text-[10px] text-[#9fd3ff]">Community</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/intelligence" className="text-xs text-zinc-400 hover:text-white transition-colors">Intelligence</Link>
            <span className="text-sm text-zinc-300 hidden sm:block">{user?.name}</span>
            <Avatar name={user?.name} size={30} />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr_300px] gap-6">
        {/* Left nav */}
        <aside className="hidden lg:block space-y-1">
          <NavItem id="home" icon={Home} label="Home Feed" />
          <NavItem id="traders" icon={Users} label="Traders" />
          <NavItem id="messages" icon={MessageSquare} label="Messages" />
          <div className="section-label text-[9px] text-zinc-600 px-3 pt-5 pb-2">Rooms</div>
          {rooms.map((r) => (
            <NavItem key={r.id} id={r.id} icon={Hash} label={r.name} />
          ))}
          <div className="section-label text-[9px] text-zinc-600 px-3 pt-5 pb-2">Voice</div>
          <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-zinc-600" data-testid="voice-coming-soon">
            <Mic className="h-4 w-4" strokeWidth={1.5} />
            <span className="font-light">Voice Rooms</span>
            <span className="ml-auto text-[8px] section-label text-[#9fd3ff] border border-[#9fd3ff]/25 rounded px-1.5 py-0.5">Soon</span>
          </div>
        </aside>

        {/* Center */}
        <section className="space-y-5 min-w-0">
          {/* mobile nav */}
          <div className="lg:hidden flex gap-2 overflow-x-auto pb-1">
            {[{ id: "home", label: "Home" }, { id: "traders", label: "Traders" }, { id: "messages", label: "Messages" }, ...rooms.map((r) => ({ id: r.id, label: r.name }))].map((n) => (
              <button key={n.id} onClick={() => setActive(n.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs border transition-colors ${active === n.id ? "bg-[#FAFAFA] text-[#0A0A0A] border-transparent" : "border-white/12 text-zinc-400"}`}>
                {n.label}
              </button>
            ))}
          </div>

          {active === "traders" ? (
            <div className="space-y-3" data-testid="traders-view">
              <h2 className="font-heading text-2xl font-light">Traders</h2>
              {traders.length === 0 && <div className="text-sm text-zinc-600">No other members yet.</div>}
              {traders.map((t) => (
                <div key={t.id} className="glass-card p-4 flex items-center gap-3" data-testid="trader-row">
                  <Avatar name={t.name} />
                  <div className="flex-1">
                    <div className="text-sm text-zinc-100 font-medium">{t.name}</div>
                    <div className="text-[11px] text-zinc-500">{t.followers} followers · {t.role.replace("_", " ")}</div>
                  </div>
                  <button onClick={() => openDM({ user_id: t.id, name: t.name })} className="text-zinc-400 hover:text-white p-2" data-testid="trader-dm-btn">
                    <MessageSquare className="h-4 w-4" />
                  </button>
                  <button onClick={() => follow(t)}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${t.following ? "border-[#7ee0a1]/40 text-[#7ee0a1]" : "border-white/15 text-zinc-300 hover:border-white/30"}`}
                    data-testid="trader-follow-btn">
                    {t.following ? <UserCheck className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                    {t.following ? "Following" : "Follow"}
                  </button>
                </div>
              ))}
            </div>
          ) : active === "messages" ? (
            <div className="space-y-3" data-testid="messages-view">
              <h2 className="font-heading text-2xl font-light">Messages</h2>
              {convos.length === 0 && <div className="text-sm text-zinc-600">No conversations yet. Message a trader from the feed or Traders tab.</div>}
              {convos.map((c) => (
                <button key={c.user_id} onClick={() => openDM(c)} className="glass-card p-4 flex items-center gap-3 w-full text-left" data-testid="convo-row">
                  <Avatar name={c.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 font-medium">{c.name}</div>
                    <div className="text-[12px] text-zinc-500 truncate">{c.last}</div>
                  </div>
                  <span className="text-[10px] text-zinc-600">{timeAgo(c.at)}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              {entitlements?.membership === "guest" ? (
                <Link to="/#pricing" className="glass-card p-5 flex items-center gap-3 hover:border-white/25 transition-colors" data-testid="community-guest-banner">
                  <div className="flex-1">
                    <div className="text-sm text-zinc-100 font-medium">Guest access — read only</div>
                    <div className="text-[12px] text-zinc-400 font-light">Subscribe to post charts, comment, follow traders and message the community.</div>
                  </div>
                  <span className="btn-solid px-4 py-2 text-xs font-medium whitespace-nowrap">View plans</span>
                </Link>
              ) : (
                <Composer room={postRoom} onPosted={() => { loadPosts(); refreshMeta(); }} />
              )}
              {posts === null ? (
                <div className="text-sm text-zinc-600 py-10 text-center">Loading feed…</div>
              ) : posts.length === 0 ? (
                <div className="glass-card p-10 text-center text-sm text-zinc-500" data-testid="empty-feed">
                  No posts here yet. Be the first to share a chart or idea.
                </div>
              ) : (
                posts.map((p) => <PostCard key={p.id} post={p} onLike={like} onDM={(post) => openDM({ user_id: post.user_id, name: post.author_name })} />)
              )}
            </>
          )}
        </section>

        {/* Right rail */}
        <aside className="hidden lg:block space-y-5">
          {challenge && (
            <div className="glass-card p-5" data-testid="weekly-challenge">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#9fd3ff]" />
                <span className="section-label text-[9px] text-zinc-300">Weekly Challenge</span>
              </div>
              <div className="text-sm text-zinc-100 font-medium">{challenge.title}</div>
              <div className="mt-1.5 text-[12px] text-zinc-400 font-light leading-relaxed">{challenge.desc}</div>
              <div className="mt-3 text-[11px] text-[#7ee0a1]">{challenge.reward}</div>
              <div className="mt-1 text-[10px] text-zinc-600">Ends {challenge.ends}</div>
            </div>
          )}

          <div className="glass-card p-5" data-testid="leaderboard">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="h-4 w-4 text-[#e6c67a]" />
              <span className="section-label text-[9px] text-zinc-300">Leaderboard</span>
            </div>
            {leaderboard.length === 0 && <div className="text-xs text-zinc-600">No activity yet.</div>}
            <div className="space-y-3">
              {leaderboard.slice(0, 8).map((l, i) => (
                <div key={i} className="flex items-center gap-3" data-testid="leaderboard-row">
                  <span className={`w-5 text-center text-xs font-heading ${i < 3 ? "text-[#e6c67a]" : "text-zinc-600"}`}>{i + 1}</span>
                  <Avatar name={l.name} size={26} />
                  <span className="flex-1 text-[13px] text-zinc-200 truncate">{l.name}</span>
                  <span className="text-[11px] text-zinc-500">{l.points} pts</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-5" data-testid="achievements">
            <div className="flex items-center gap-2 mb-4">
              <Medal className="h-4 w-4 text-[#9fd3ff]" />
              <span className="section-label text-[9px] text-zinc-300">Your Badges</span>
            </div>
            <div className="space-y-2.5">
              {achievements.map((a) => (
                <div key={a.id} className="flex items-center gap-2.5" data-testid="achievement-row">
                  <Flame className={`h-3.5 w-3.5 ${a.earned ? "text-[#ff9d5c]" : "text-zinc-700"}`} />
                  <span className={`text-[13px] ${a.earned ? "text-zinc-200" : "text-zinc-600"}`}>{a.name}</span>
                  {a.earned && <span className="ml-auto text-[9px] section-label text-[#7ee0a1]">Earned</span>}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>

      {dmTarget && <DMDrawer target={dmTarget} onClose={() => setDmTarget(null)} />}
    </div>
  );
}
